import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/axios';
import { API_BASE_URL } from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { SiteHeader } from './site-header';

// Add CSS animations
const rippleKeyframes = `
  @keyframes ripple {
    to {
      transform: scale(4);
      opacity: 0;
    }
  }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = rippleKeyframes;
  document.head.appendChild(style);
}

const accent = '#fd680e';

const ECSEmployeeDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);
  
  // Associate request management states
  const [associateRequests, setAssociateRequests] = useState([]);
  const [associateRequestsLoading, setAssociateRequestsLoading] = useState(false);
  const [associateRequestsError, setAssociateRequestsError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    status: 'approved',
    review_notes: '',
    password: ''
  });

  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  const [associatesError, setAssociatesError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Enhanced freelancer management
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'associate-requests', 'associates', 'freelancers'
  
  // Sidebar collapse state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };
  const [freelancers, setFreelancers] = useState([]);
  const [freelancersLoading, setFreelancersLoading] = useState(false);
  const [freelancersError, setFreelancersError] = useState('');
  const [freelancerFilters] = useState({
    availability_status: 'all',
    approval_status: 'all',
    page: 1,
    limit: 10
  });
  
  // Enhanced statistics
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  
  // Real-time monitoring states
  const [recentHires, setRecentHires] = useState([]);
  const [recentHiresLoading, setRecentHiresLoading] = useState(false);

  // Freelancer request management
  const [freelancerRequests, setFreelancerRequests] = useState([]);
  const [freelancerRequestsLoading, setFreelancerRequestsLoading] = useState(false);
  const [freelancerRequestsError, setFreelancerRequestsError] = useState('');
  const [selectedFreelancerRequest, setSelectedFreelancerRequest] = useState(null);
  const [showFreelancerRequestDetailsModal, setShowFreelancerRequestDetailsModal] = useState(false);
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false);
  const [selectedFreelancers, setSelectedFreelancers] = useState([]);
  const [highlightedFreelancers, setHighlightedFreelancers] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [allFreelancers, setAllFreelancers] = useState([]);
  const [availableFreelancers, setAvailableFreelancers] = useState([]);
  const [recommendationSuccessMessage, setRecommendationSuccessMessage] = useState('');
  
  // Simple search state
  const [searchSkills, setSearchSkills] = useState('');
  const [searchExperience, setSearchExperience] = useState('');
  const [searchStatus, setSearchStatus] = useState('all');

  // Freelancer profile modal states
  const [showFreelancerProfileModal, setShowFreelancerProfileModal] = useState(false);
  const [selectedFreelancerProfile, setSelectedFreelancerProfile] = useState(null);
  const [freelancerProfileLoading, setFreelancerProfileLoading] = useState(false);



  // Analytics data states




  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Aggressively disable AOS animations for this component to prevent unwanted spinning
  useEffect(() => {
    // First, override the global AOS initialization to prevent it from running
    const originalAOSInit = window.AOS?.init;
    if (window.AOS && originalAOSInit) {
      window.AOS.init = function(options) {
        // Only initialize AOS if it's not for dashboard pages
        if (!window.location.pathname.includes('dashboard')) {
          return originalAOSInit.call(this, options);
        }
        // For dashboard pages, do nothing (disable AOS completely)
        return;
      };
    }

    const disableAOS = () => {
      // Disable AOS for the entire document when on dashboard pages
      if (window.location.pathname.includes('dashboard')) {
        // Remove all AOS attributes from the entire document
        document.querySelectorAll('[data-aos]').forEach(element => {
          element.removeAttribute('data-aos');
          element.setAttribute('data-aos', 'none');
          element.classList.remove('aos-init', 'aos-animate');
          element.style.animation = 'none';
          element.style.transform = 'none';
          element.style.opacity = '1';
          element.style.visibility = 'visible';
        });
      }

      // Specifically target the sidebar and all its children
      const sidebar = document.querySelector('.ecs-employee-sidebar');
      if (sidebar) {
        // Remove any existing AOS attributes and disable animations
        sidebar.removeAttribute('data-aos');
        sidebar.setAttribute('data-aos', 'none');
        
        // Also disable for all child elements
        const children = sidebar.querySelectorAll('*');
        children.forEach(child => {
          child.removeAttribute('data-aos');
          child.setAttribute('data-aos', 'none');
          // Force remove any animation classes
          child.classList.remove('aos-init', 'aos-animate');
          // Apply inline styles to prevent animations
          child.style.animation = 'none';
          child.style.transform = 'none';
          child.style.opacity = '1';
          child.style.visibility = 'visible';
        });
        
        // For buttons, only disable AOS but preserve functionality
        const buttons = sidebar.querySelectorAll('button');
        buttons.forEach(button => {
          button.setAttribute('data-aos', 'none');
          button.classList.remove('aos-init', 'aos-animate');
          // Ensure buttons remain interactive
          button.style.pointerEvents = 'auto';
          button.style.cursor = 'pointer';
          button.style.animation = 'none';
          button.style.transform = 'none';
          button.style.opacity = '1';
        });
        
        // If AOS is loaded globally, refresh it to apply the changes
        if (window.AOS) {
          window.AOS.refresh();
        }
      }
    };

    // Run immediately and with multiple delays to ensure DOM is ready
    disableAOS();
    const timeoutId1 = setTimeout(disableAOS, 50);
    const timeoutId2 = setTimeout(disableAOS, 100);
    const timeoutId3 = setTimeout(disableAOS, 200);
    const timeoutId4 = setTimeout(disableAOS, 500);
    const timeoutId5 = setTimeout(disableAOS, 1000);
    
    // Also run on window load to override the global AOS init
    const handleLoad = () => {
      disableAOS();
    };
    window.addEventListener('load', handleLoad);
    
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(timeoutId4);
      clearTimeout(timeoutId5);
      window.removeEventListener('load', handleLoad);
      
      // Restore original AOS init function
      if (window.AOS && originalAOSInit) {
        window.AOS.init = originalAOSInit;
      }
    };
  }, []);

  // Load user data on component mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData || userData.user_type !== 'ecs_employee') {
          navigate('/login');
          return;
        }
        setUser(userData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/login');
      }
    };

    loadUserData();
  }, [navigate]);

  // Load statistics and real-time data for home tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadStats();
      fetchRecentHires();
      fetchPendingFreelancerRequestsCount(); // Fetch pending freelancer requests count
    }
  }, [activeTab]);

  // Auto-refresh data every 2 minutes for real-time updates
  useEffect(() => {
    let intervalId;
    
    if (activeTab === 'dashboard') {
      intervalId = setInterval(() => {
        fetchRecentHires();
        fetchPendingFreelancerRequestsCount(); // Refresh pending freelancer requests count
      }, 2 * 60 * 1000); // 2 minutes
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [activeTab]);

  // Load associate requests
  useEffect(() => {
    if (activeTab === 'associate-requests') {
      loadAssociateRequests();
    }
  }, [activeTab]);

  // Load associates
  useEffect(() => {
    if (activeTab === 'associates') {
      loadAssociates();
    }
  }, [activeTab, loadAssociates]);

  // Load freelancers
  useEffect(() => {
    if (activeTab === 'freelancers') {
      loadFreelancers();
    }
  }, [activeTab, loadFreelancers]);

  // Load freelancer requests
  useEffect(() => {
    if (activeTab === 'freelancer-requests') {
      fetchFreelancerRequests();
    }
  }, [activeTab, fetchFreelancerRequests]);







  const loadStats = async () => {
    setStatsLoading(true);
    setStatsError('');
    try {
      const response = await api.get('/admin/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      setStatsError('Failed to load statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch recent hires (associate responses to freelancer recommendations)
  const fetchRecentHires = async () => {
    setRecentHiresLoading(true);
    try {
      const response = await api.get('/hiring/recent-hires');
      if (response.data.success) {
        setRecentHires(response.data.hires);
      }
    } catch (error) {
      console.error('Error fetching recent hires:', error);
      // Fallback to local data if API fails
      setRecentHires([]);
    } finally {
      setRecentHiresLoading(false);
    }
  };

  // Fetch pending associate freelancer requests count
  const fetchPendingFreelancerRequestsCount = async () => {
    try {
      const response = await api.get('/admin/associate-requests?status=pending');
      if (response.data.success) {
        // Update the freelancerRequests state with pending requests only
        setFreelancerRequests(response.data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching pending freelancer requests count:', error);
      // Keep existing data if API fails
    }
  };

  const loadAssociateRequests = async () => {
    setAssociateRequestsLoading(true);
    setAssociateRequestsError('');
    try {
      const response = await api.get('/associate-request/requests');
      if (response.data.success) {
        setAssociateRequests(response.data.data.requests);
      } else {
        setAssociateRequestsError(response.data.message || 'Failed to load associate requests');
      }
    } catch (error) {
      console.error('Error loading associate requests:', error);
      setAssociateRequestsError('Failed to load associate requests');
    } finally {
      setAssociateRequestsLoading(false);
    }
  };

  const loadAssociates = useCallback(async () => {
    setAssociatesLoading(true);
    setAssociatesError('');
    try {
      const response = await api.get('/admin/associates');
      if (response.data.success) {
        setAssociates(response.data.associates);
      }
    } catch (error) {
      console.error('Error loading associates:', error);
      setAssociatesError('Failed to load associates');
    } finally {
      setAssociatesLoading(false);
    }
  }, []);

  const loadFreelancers = useCallback(async () => {
    setFreelancersLoading(true);
    setFreelancersError('');
    try {
      const queryParams = new URLSearchParams({
        availability_status: freelancerFilters.availability_status,
        approval_status: freelancerFilters.approval_status,
        page: freelancerFilters.page,
        limit: freelancerFilters.limit
      });

      const response = await api.get(`/admin/freelancers?${queryParams}`);
      if (response.data.success) {
        setFreelancers(response.data.freelancers);
      }
    } catch (error) {
      console.error('Error loading freelancers:', error);
      setFreelancersError('Failed to load freelancers');
    } finally {
      setFreelancersLoading(false);
    }
  }, [freelancerFilters]);

  // Associate Request Management Functions
  const handleReviewRequest = async (requestId) => {
    setReviewLoading(true);
    try {
      const res = await api.put(`/associate-request/requests/${requestId}/review`, reviewFormData);
      
      if (res.data.success) {
        // Refresh the requests list
        loadAssociateRequests();
    setSelectedRequest(null);
        setReviewFormData({ status: 'approved', review_notes: '', password: '' });
        setSuccessMessage(`Request ${reviewFormData.status} successfully`);
        setTimeout(() => setSuccessMessage(''), 5000); // Clear after 5 seconds
      } else {
        setErrorMessage(res.data.message || 'Failed to review request');
        setTimeout(() => setErrorMessage(''), 5000); // Clear after 5 seconds
      }
    } catch (err) {
      console.error('❌ Review request error:', err);
      console.error('Error response:', err.response?.data);
      setErrorMessage(err.response?.data?.message || 'Failed to review request');
      setTimeout(() => setErrorMessage(''), 5000); // Clear after 5 seconds
    } finally {
      setReviewLoading(false);
    }
  };

  const handleReviewFormChange = (e) => {
    setReviewFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Freelancer Request Management Functions
  const fetchFreelancerRequests = useCallback(async () => {
    setFreelancerRequestsLoading(true);
    setFreelancerRequestsError('');
    try {
      const res = await api.get('/admin/associate-requests');
      if (res.data.success) {
        setFreelancerRequests(res.data.requests);
      } else {
        setFreelancerRequestsError(res.data.message || 'Failed to fetch freelancer requests');
      }
    } catch (err) {
      setFreelancerRequestsError(err.response?.data?.message || 'Failed to fetch freelancer requests');
    } finally {
      setFreelancerRequestsLoading(false);
    }
  }, []);

  const fetchAvailableFreelancers = async () => {
    try {
      // Get ALL freelancers for comprehensive recommendations
      const res = await api.get('/admin/freelancers?availability_status=all&approval_status=all');

      if (res.data.success) {
        const freelancers = res.data.freelancers || [];
        setAllFreelancers(freelancers);
        setAvailableFreelancers(freelancers);
      } else {
        console.error('API returned success: false:', res.data);
        console.error('Error message:', res.data.message);
      }
    } catch (err) {
      console.error('Error fetching available freelancers:', err);
      console.error('Error details:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error message:', err.message);

      // Set empty arrays to prevent UI errors
      setAllFreelancers([]);
      setAvailableFreelancers([]);
    }
  };

  const openRecommendationsModal = async (request) => {
    setSelectedFreelancerRequest(request);
    setShowRecommendationsModal(true);
    setSelectedFreelancers([]);
    setHighlightedFreelancers([]);
    setAdminNotes('');
    
    // Fetch all freelancers when opening recommendations modal
    await fetchAvailableFreelancers();
    
    // Disable AOS animations for the modal and its contents
    setTimeout(() => {
      const modal = document.querySelector('.modal.show');
      if (modal) {
        modal.setAttribute('data-aos', 'none');
        const cards = modal.querySelectorAll('.card');
        cards.forEach(card => {
          card.setAttribute('data-aos', 'none');
          card.classList.remove('aos-init', 'aos-animate');
        });
        
        // If AOS is loaded globally, refresh it to apply the changes
        if (window.AOS) {
          window.AOS.refresh();
        }
      }
    }, 100);
  };

  const handleFreelancerSelection = (freelancerId, isSelected) => {
    if (isSelected) {
      setSelectedFreelancers(prev => {
        const newSelection = [...prev, freelancerId];
        return newSelection;
      });
    } else {
      setSelectedFreelancers(prev => {
        const newSelection = prev.filter(id => id !== freelancerId);
        return newSelection;
      });
    }
  };

  const handleHighlightFreelancer = (freelancerId, isHighlighted) => {
    if (isHighlighted) {
      setHighlightedFreelancers(prev => [...prev, freelancerId]);
    } else {
      setHighlightedFreelancers(prev => prev.filter(id => id !== freelancerId));
    }
  };

  // Freelancer profile modal functions
  const openFreelancerProfile = async (freelancer) => {
    setSelectedFreelancerProfile(freelancer);
    setShowFreelancerProfileModal(true);
    
    // Fetch detailed profile data
    setFreelancerProfileLoading(true);
    try {
      const res = await api.get(`/admin/freelancers/${freelancer.freelancer_id}/profile`);
      if (res.data.success) {
        setSelectedFreelancerProfile(res.data.freelancer);
      } else {
        console.error('❌ Failed to fetch freelancer profile:', res.data.message);
        // Still show modal with basic data if detailed fetch fails
      }
    } catch (err) {
      console.error('❌ Error fetching freelancer profile:', err);
      // Still show modal with basic data if detailed fetch fails
    } finally {
      setFreelancerProfileLoading(false);
    }
  };

  const submitRecommendations = async () => {
    if (selectedFreelancers.length === 0) {
      alert('Please select at least one freelancer');
      return;
    }

    if (!selectedFreelancerRequest || !selectedFreelancerRequest.request_id) {
      alert('No request selected for recommendations');
      return;
    }

    // Validate freelancer data
    const validFreelancers = selectedFreelancers.filter(id => {
      const freelancer = allFreelancers.find(f => f.freelancer_id === id);
      return freelancer && freelancer.freelancer_id;
    });

    if (validFreelancers.length !== selectedFreelancers.length) {
      alert('Some selected freelancers are invalid. Please refresh and try again.');
      return;
    }

    setRecommendationsLoading(true);
    try {
      const res = await api.post(`/admin/associate-requests/${selectedFreelancerRequest.request_id}/recommendations`, {
        freelancer_ids: selectedFreelancers,
        admin_notes: adminNotes,
        highlighted_freelancers: highlightedFreelancers
      });

      if (res.data.success) {
        setRecommendationSuccessMessage(`Successfully submitted ${selectedFreelancers.length} recommendation${selectedFreelancers.length !== 1 ? 's' : ''}!`);
        setShowRecommendationsModal(false);
        setSelectedFreelancers([]);
        setHighlightedFreelancers([]);
        setAdminNotes('');
        await fetchFreelancerRequests();
        
        // Clear success message after 5 seconds
        setTimeout(() => setRecommendationSuccessMessage(''), 5000);
      } else {
        console.error('❌ API returned success: false:', res.data);
        alert(res.data.message || 'Failed to submit recommendations');
      }
    } catch (err) {
      console.error('❌ Error submitting recommendations:', err);
      console.error('Error response:', err.response?.data);
      alert(err.response?.data?.message || 'Failed to submit recommendations. Please try again.');
    } finally {
      setRecommendationsLoading(false);
    }
  };

    // Simple search function
  const handleSearch = () => {
    let filtered = [...allFreelancers];

    // Filter by skills
    if (searchSkills && searchSkills.trim()) {
      filtered = filtered.filter(f => {
        const hasSkills = f.skills?.some(skill => {
          const skillName = skill.skill_name || skill;
          return skillName && skillName.toLowerCase().includes(searchSkills.toLowerCase());
        });
        const hasHeadline = f.headline?.toLowerCase().includes(searchSkills.toLowerCase());
        const hasCurrentStatus = f.current_status?.toLowerCase().includes(searchSkills.toLowerCase());
        return hasSkills || hasHeadline || hasCurrentStatus;
      });
    }

    // Filter by experience
    if (searchExperience && searchExperience.trim()) {
      const minExp = parseInt(searchExperience);
      if (!isNaN(minExp)) {
        filtered = filtered.filter(f => {
          const experience = f.experience_years || 0;
          return experience >= minExp;
        });
      }
    }

    // Filter by status
    if (searchStatus && searchStatus !== 'all') {
      filtered = filtered.filter(f => {
        const status = f.availability_status || 'available';
        return status === searchStatus;
      });
    }

    setAvailableFreelancers(filtered);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    window.scrollTo(0, 0);
  };

  // Add hover state management for better UX
  const [, setHoveredTab] = useState(null);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <SiteHeader 
        title="CV-Connect ECS Employee Portal" 
        onToggleSidebar={toggleSidebar}
        isSidebarCollapsed={isSidebarCollapsed}
        userName="ECS Employee"
        userRole="Employee"
      />
      {/* Professional Sidebar */}
      <div className="sidebar ecs-employee-sidebar" data-aos="none" style={{ 
        width: isSidebarCollapsed ? '60px' : '280px', 
        height: '100vh', 
        position: 'fixed', 
        left: 0, 
        top: 0, 
        background: '#fff', 
        borderRight: '2px solid rgba(253, 104, 14, 0.2)',
        overflowY: isSidebarCollapsed ? 'hidden' : 'auto',
        overflowX: 'hidden',
        zIndex: 1000,
        animation: 'none',
        transform: 'none',
        opacity: 1,
        visibility: 'visible',
        transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '2px 0 6px rgba(253, 104, 14, 0.1)'
      }}>
        {/* Sidebar Header */}
        <div className="sidebar-header" data-aos="none" style={{ 
          padding: isSidebarCollapsed ? '12px' : '24px', 
          borderBottom: '1px solid #e5e7eb',
          background: '#fafafa',
          animation: 'none',
          transform: 'none',
          opacity: 1,
          visibility: 'visible',
          transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div className="d-flex align-items-center justify-content-center" data-aos="none" style={{ 
            animation: 'none',
            transform: 'none',
            opacity: 1,
            visibility: 'visible'
          }}>
            <img 
              src="/assets/img/cv-connect_logo.png" 
              alt="CV-Connect Logo"
              data-aos="none"
              style={{ 
                animation: 'none',
                transform: 'none',
                opacity: 1,
                visibility: 'visible',
                width: '32px', 
                height: '32px', 
                borderRadius: '8px',
                marginRight: isSidebarCollapsed ? '0' : '12px'
              }}
            />
            {!isSidebarCollapsed && (
        <div data-aos="none">
              <h5 className="mb-0" style={{ color: '#111827', fontWeight: 600, fontSize: '16px' }} data-aos="none">
                CV-Connect
              </h5>
              <small className="text-muted" data-aos="none">ECS Employee Portal</small>
          </div>
            )}
          </div>
        </div>

        {/* Sidebar Content */}
        <div className="sidebar-content" data-aos="none" style={{ 
          padding: '16px', 
          display: 'flex', 
          flexDirection: 'column', 
          height: 'calc(100vh - 120px)' 
        }}>
          {/* Main Navigation */}
          <div className="nav-section mb-4" data-aos="none">
            {!isSidebarCollapsed && (
            <h6 className="nav-section-title" style={{ 
              color: '#6b7280', 
              fontSize: '12px', 
              fontWeight: 600, 
              textTransform: 'uppercase',
              marginBottom: '8px',
              paddingLeft: '8px'
            }} data-aos="none">
              Main Navigation
            </h6>
            )}
            <div className="nav-items" data-aos="none" style={{ 
              animation: 'none',
              transform: 'none',
              opacity: 1,
              visibility: 'visible'
            }}>
              <button
                className={`nav-item w-100 text-start ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => handleTabChange('dashboard')}
                onMouseEnter={() => setHoveredTab('dashboard')}
                onMouseLeave={() => setHoveredTab(null)}
                data-aos="none"
                style={{
                  padding: isSidebarCollapsed ? '12px' : '12px 16px',
                  border: activeTab === 'dashboard' ? `2px solid ${accent}` : '2px solid rgba(253, 104, 14, 0.2)',
                  background: activeTab === 'dashboard' ? accent : 'transparent',
                  color: activeTab === 'dashboard' ? '#fff' : '#374151',
                  borderRadius: '8px',
                  animation: 'none',
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible',
                  marginBottom: '4px',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  boxShadow: activeTab === 'dashboard' ? '0 4px 12px rgba(253, 104, 14, 0.25)' : '0 2px 6px rgba(253, 104, 14, 0.1)'
                }}
                title={isSidebarCollapsed ? 'Home' : ''}
              >
                <i className={`bi bi-house-door ${!isSidebarCollapsed ? 'me-3' : ''}`} data-aos="none"></i>
                {!isSidebarCollapsed && 'Home'}
        </button>
              


              <button
                className={`nav-item w-100 text-start ${activeTab === 'associate-requests' ? 'active' : ''}`}
                onClick={() => handleTabChange('associate-requests')}
                onMouseEnter={() => setHoveredTab('associate-requests')}
                onMouseLeave={() => setHoveredTab(null)}
                data-aos="none"
                style={{
                  padding: isSidebarCollapsed ? '12px' : '12px 16px',
                  border: activeTab === 'associate-requests' ? `2px solid ${accent}` : '2px solid rgba(253, 104, 14, 0.2)',
                  background: activeTab === 'associate-requests' ? accent : 'transparent',
                  color: activeTab === 'associate-requests' ? '#fff' : '#374151',
                  borderRadius: '8px',
                  animation: 'none',
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible',
                  marginBottom: '4px',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  height: '48px',
                  lineHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  boxShadow: activeTab === 'associate-requests' ? '0 4px 12px rgba(253, 104, 14, 0.25)' : '0 2px 6px rgba(253, 104, 14, 0.1)'
                }}
                title={isSidebarCollapsed ? 'Associate Requests' : ''}
              >
                <i className={`bi bi-person-plus ${!isSidebarCollapsed ? 'me-3' : ''}`} data-aos="none"></i>
                {!isSidebarCollapsed && 'Associate Requests'}
                </button>

              <button
                className={`nav-item w-100 text-start ${activeTab === 'freelancer-requests' ? 'active' : ''}`}
                onClick={() => handleTabChange('freelancer-requests')}
                onMouseEnter={() => setHoveredTab('freelancer-requests')}
                onMouseLeave={() => setHoveredTab(null)}
                data-aos="none"
                style={{
                  padding: isSidebarCollapsed ? '12px' : '12px 16px',
                  border: activeTab === 'freelancer-requests' ? `2px solid ${accent}` : '2px solid rgba(253, 104, 14, 0.2)',
                  background: activeTab === 'freelancer-requests' ? accent : 'transparent',
                  color: activeTab === 'freelancer-requests' ? '#fff' : '#374151',
                  borderRadius: '8px',
                  animation: 'none',
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible',
                  marginBottom: '4px',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  height: '48px',
                  lineHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  boxShadow: activeTab === 'freelancer-requests' ? '0 4px 12px rgba(253, 104, 14, 0.25)' : '0 2px 6px rgba(253, 104, 14, 0.1)'
                }}
                title={isSidebarCollapsed ? 'Associate Freelancer Requests' : ''}
              >
                <i className={`bi bi-people-fill ${!isSidebarCollapsed ? 'me-3' : ''}`} data-aos="none"></i>
                {!isSidebarCollapsed && 'Associate Freelancer Requests'}
                </button>




        </div>
      </div>
      
          {/* Spacer to push bottom section down */}
          <div style={{ flex: 1 }}></div>

          {/* Bottom Section - Settings and Logout */}
          {/* System */}
          <div className="nav-section" data-aos="none">
            {!isSidebarCollapsed && (
            <h6 className="nav-section-title" style={{ 
              color: '#6b7280', 
              fontSize: '12px', 
              fontWeight: 600, 
              textTransform: 'uppercase',
              marginBottom: '8px',
              paddingLeft: '8px'
            }} data-aos="none">
              System
            </h6>
            )}
            <div className="nav-items" data-aos="none" style={{ 
              animation: 'none',
              transform: 'none',
              opacity: 1,
              visibility: 'visible'
            }}>
              <button
                className={`nav-item w-100 text-start ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => handleTabChange('settings')}
                onMouseEnter={() => setHoveredTab('settings')}
                onMouseLeave={() => setHoveredTab(null)}
                data-aos="none"
                style={{
                  padding: isSidebarCollapsed ? '12px' : '12px 16px',
                  border: activeTab === 'settings' ? `2px solid ${accent}` : '2px solid rgba(253, 104, 14, 0.2)',
                  background: activeTab === 'settings' ? accent : 'transparent',
                  color: activeTab === 'settings' ? '#fff' : '#374151',
                  borderRadius: '8px',
                  animation: 'none',
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible',
                  marginBottom: '4px',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  height: '48px',
                  lineHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  boxShadow: activeTab === 'settings' ? '0 4px 12px rgba(253, 104, 14, 0.25)' : '0 2px 6px rgba(253, 104, 14, 0.1)'
                }}
                title={isSidebarCollapsed ? 'Settings' : ''}
              >
                <i className={`bi bi-gear ${!isSidebarCollapsed ? 'me-3' : ''}`} data-aos="none"></i>
                {!isSidebarCollapsed && 'Settings'}
              </button>
              
                <button
                className="nav-item w-100 text-start logout-button"
                onClick={logout}
                onMouseEnter={() => setHoveredTab('logout')}
                onMouseLeave={() => setHoveredTab(null)}
                data-aos="none"
                style={{
                  padding: '12px 16px',
                  border: '2px solid rgba(220, 53, 69, 0.2)',
                  background: 'transparent',
                  color: '#dc3545',
                  borderRadius: '8px',
                  animation: 'none',
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible',
                  marginBottom: '4px',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  height: '48px',
                  lineHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 2px 6px rgba(220, 53, 69, 0.1)'
                }}
              >
                <i className="bi bi-box-arrow-right me-3" data-aos="none"></i>
                Logout
                </button>
            </div>
          </div>
                </div>
              </div>

      {/* Main Content Area */}
      <div className="main-content flex-grow-1" style={{ 
        marginLeft: isSidebarCollapsed ? '60px' : '280px',
        padding: '20px',
        paddingTop: '80px', // Account for fixed header height (60px + 20px spacing)
        transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        minHeight: '100vh',
        background: '#f9fafb',
        width: isSidebarCollapsed ? 'calc(100% - 60px)' : 'calc(100% - 280px)',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-0" style={{ color: '#111827', fontWeight: 600 }}>
              {activeTab === 'dashboard' && 'Home'}
              {activeTab === 'associate-requests' && 'Associate Requests'}
              {activeTab === 'freelancer-requests' && 'Associate Freelancer Requests'}
              {activeTab === 'settings' && 'Settings'}
            </h1>
            <p className="text-muted mb-0">
              {activeTab === 'dashboard' && 'Operational tasks and associate response tracking'}
              {activeTab === 'associate-requests' && 'Review associate join requests'}
              {activeTab === 'freelancer-requests' && 'Handle associate freelancer requests'}
              {activeTab === 'settings' && 'System configuration and preferences'}
            </p>
            </div>
        </div>
        {/* Main Dashboard Content */}
        <div className="px-3 py-4" style={{ width: '100%', maxWidth: '100%' }}>
          {/* Home Tab - Operational Center */}
          {activeTab === 'dashboard' && (
            <>
              {/* Welcome Section */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="bg-white rounded-4 shadow-sm p-4" style={{ boxShadow: '0 2px 6px rgba(253, 104, 14, 0.1)', border: '2px solid rgba(253, 104, 14, 0.2)' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h4 className="mb-2" style={{ color: accent, fontWeight: 600 }}>
                          <i className="bi bi-house-door me-2"></i>
                          Welcome back, {user?.first_name || 'ECS Employee'}!
                        </h4>
                        <p className="text-muted mb-0">
                          Manage associate requests, handle freelancer recommendations, and track associate responses
                        </p>
                      </div>
                      <div className="text-end">
                        <div className="h5 mb-1" style={{ color: accent }}>
                          {new Date().toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                        <small className="text-muted">Ready to work</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions Row */}
              <div className="row g-4 mb-4">
                <div className="col-lg-4 col-md-6">
                  <div className="bg-white rounded-4 shadow-sm p-4 text-center" style={{ boxShadow: '0 2px 6px rgba(253, 104, 14, 0.1)', border: '2px solid rgba(253, 104, 14, 0.2)' }}>
                    <div className="mb-3">
                      <div style={{ fontSize: 32, color: accent, marginBottom: 8 }}>
                        <i className="bi bi-envelope"></i>
                      </div>
                      <div style={{ color: '#6c757d', fontSize: '14px', fontWeight: 500, textTransform: 'uppercase' }}>
                        Associate Pending Requests
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '28px', color: '#111827', marginBottom: '8px' }}>
                      {statsLoading ? '...' : statsError ? '--' : stats?.associate_requests?.pending ?? '--'}
                    </div>
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <span className="badge" style={{ background: '#f59e0b', color: '#fff', fontSize: '12px', padding: '4px 8px' }}>
                        <i className="bi bi-clock me-1"></i>
                        Awaiting
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-muted">
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                        <i className="bi bi-clock" style={{ color: '#f59e0b' }}></i>
                        <span style={{ fontWeight: 500 }}>Review required</span>
                      </div>
                      <div className="text-muted">ECS Employee attention needed</div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-md-6">
                  <div className="bg-white rounded-4 shadow-sm p-4 text-center" style={{ boxShadow: '0 2px 6px rgba(253, 104, 14, 0.1)', border: '2px solid rgba(253, 104, 14, 0.2)' }}>
                    <div className="mb-3">
                      <div style={{ fontSize: 32, color: accent, marginBottom: 8 }}>
                        <i className="bi bi-people-fill"></i>
                      </div>
                      <div style={{ color: '#6c757d', fontSize: '14px', fontWeight: 500, textTransform: 'uppercase' }}>
                        Pending Associate Freelancer Requests
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '28px', color: '#111827', marginBottom: '8px' }}>
                      {freelancerRequestsLoading ? '...' : freelancerRequests.length === 0 ? '0' : freelancerRequests.length || '--'}
                    </div>
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <span className="badge" style={{ background: '#f59e0b', color: '#fff', fontSize: '12px', padding: '4px 8px' }}>
                        <i className="bi bi-hand-thumbs-up me-1"></i>
                        Active
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-muted">
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                        <i className="bi bi-people" style={{ color: accent }}></i>
                        <span style={{ fontWeight: 500 }}>Pending requests</span>
                      </div>
                      <div className="text-muted">Awaiting freelancer recommendations</div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-md-6">
                  <div className="bg-white rounded-4 shadow-sm p-4 text-center" style={{ boxShadow: '0 2px 6px rgba(253, 104, 14, 0.1)', border: '2px solid rgba(253, 104, 14, 0.2)' }}>
                    <div className="mb-3">
                      <div style={{ fontSize: 32, color: accent, marginBottom: 8 }}>
                        <i className="bi bi-briefcase"></i>
                      </div>
                      <div style={{ color: '#6c757d', fontSize: '14px', fontWeight: 500, textTransform: 'uppercase' }}>
                        Recent Responses
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '28px', color: '#111827', marginBottom: '8px' }}>
                      {recentHiresLoading ? '...' : recentHires.length || '0'}
                    </div>
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <span className="badge" style={{ background: '#f59e0b', color: '#fff', fontSize: '12px', padding: '4px 8px' }}>
                        <i className="bi bi-chat-dots me-1"></i>
                        New
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-muted">
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                        <i className="bi bi-briefcase" style={{ color: accent }}></i>
                        <span style={{ fontWeight: 500 }}>Associate feedback</span>
                      </div>
                      <div className="text-muted">Track recommendation responses</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Hires - Associate Responses */}
              <div className="row g-4">
                <div className="col-12">
                  <div className="bg-white rounded-4 shadow-sm p-4" style={{ boxShadow: '0 2px 6px rgba(253, 104, 14, 0.1)', border: '2px solid rgba(253, 104, 14, 0.2)' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0" style={{ color: accent, fontWeight: 600 }}>
                        <i className="bi bi-briefcase me-2"></i>
                        Recent Freelancer Hires
                      </h5>
                      <button 
                        className="btn btn-sm btn-outline-secondary"
                        onClick={fetchRecentHires}
                        disabled={recentHiresLoading}
                      >
                        {recentHiresLoading ? '...' : 'Refresh'}
                      </button>
                    </div>
                    {recentHiresLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
                    ) : recentHires.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-inbox fs-1"></i>
                        <p className="mt-2">No recent hires yet</p>
                        <small>When associates hire freelancers, the information will appear here</small>
        </div>
      ) : (
                      <div className="space-y-3">
                        {recentHires.slice(0, 8).map((hire, index) => (
                          <div key={index} className="d-flex align-items-center p-3 border rounded" style={{ background: '#f8f9fa' }}>
                            <div className="flex-shrink-0 me-3">
                              <div style={{ 
                                width: '40px', 
                                height: '40px', 
                                background: '#10b981',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff'
                              }}>
                                <i className="bi bi-check-lg"></i>
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <div className="fw-bold">{hire.associate_name}</div>
                              <div className="text-muted small">
                                {hire.freelancer_first_name} {hire.freelancer_last_name} • {hire.freelancer_role}
                              </div>
                              <div className="text-muted small">
                                {hire.project_title}
                              </div>
                              <div className="text-muted small">
                                {new Date(hire.hire_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </div>
                            </div>
                            <div className="text-end">
                              <span className="badge bg-success">
                                Successful
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Associate Requests Tab */}
          {activeTab === 'associate-requests' && (
            <div className="bg-white rounded-4 shadow-sm p-4" style={{ boxShadow: '0 2px 6px rgba(253, 104, 14, 0.1)', border: '2px solid rgba(253, 104, 14, 0.2)', maxWidth: 1200, margin: '0 auto' }}>
              <h5 style={{ color: accent, fontWeight: 700, marginBottom: 18 }}>Associate Requests</h5>
              
              {/* Success and Error Messages */}
              {successMessage && (
                <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
                  <i className="bi bi-check-circle me-2"></i>
                  {successMessage}
                  <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
                </div>
              )}
              
              {errorMessage && (
                <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errorMessage}
                  <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
                </div>
              )}
              {associateRequestsLoading ? (
                <div>Loading associate requests...</div>
              ) : associateRequestsError ? (
                <div style={{ color: '#df1529', fontWeight: 500 }}>{associateRequestsError}</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="table table-bordered" style={{ minWidth: 700 }}>
                    <thead style={{ background: '#f8f9fa' }}>
                      <tr>
                <th>Email</th>
                        <th>Company Name</th>
                        <th>Contact Person</th>
                <th>Industry</th>
                        <th>Phone</th>
                <th>Status</th>
                        <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
                      {associateRequests.map(request => (
                <tr key={request.request_id}>
                  <td>{request.email}</td>
                          <td>{request.company_name || 'N/A'}</td>
                          <td>{request.contact_person}</td>
                  <td>{request.industry}</td>
                          <td>{request.phone}</td>
                  <td>
                            <span className={`badge ${request.status === 'pending' ? 'bg-warning' : request.status === 'approved' ? 'bg-success' : 'bg-danger'}`}>
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </td>
                          <td>{request.created_at ? new Date(request.created_at).toLocaleDateString() : ''}</td>
                  <td>
                    {request.status === 'pending' && (
                        <button 
                                className="btn btn-sm btn-primary me-2"
                                onClick={() => setSelectedRequest(request)}
                                style={{ borderRadius: 20, fontWeight: 600, fontSize: 14, padding: '6px 18px' }}
                        >
                                Review
                        </button>
                            )}
                            {request.status !== 'pending' && (
                              <div>
                                <small className="text-muted">
                                  {request.reviewed_at ? `Reviewed: ${new Date(request.reviewed_at).toLocaleDateString()}` : ''}
                                </small>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

              {/* Review Modal */}
              {selectedRequest && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050 }}>
                  <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Review Associate Request</h5>
                        <button type="button" className="btn-close" onClick={() => setSelectedRequest(null)}></button>
                      </div>
                      <div className="modal-body">
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <strong>Email:</strong> {selectedRequest.email}
                          </div>
                          <div className="col-md-6">
                            <strong>Company:</strong> {selectedRequest.company_name || 'N/A'}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <strong>Contact Person:</strong> {selectedRequest.contact_person}
                          </div>
                          <div className="col-md-6">
                            <strong>Industry:</strong> {selectedRequest.industry}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <strong>Phone:</strong> {selectedRequest.phone}
                          </div>
                          <div className="col-md-6">
                            <strong>Website:</strong> {selectedRequest.website || 'N/A'}
                          </div>
                        </div>
                        {selectedRequest.request_reason && (
                          <div className="mb-3">
                            <strong>Reason:</strong> {selectedRequest.request_reason}
                          </div>
                        )}
                        <hr />
                        <form>
                          <div className="mb-3">
                            <label className="form-label">Decision:</label>
                            <select 
                              name="status" 
                              className="form-select" 
                              value={reviewFormData.status}
                              onChange={handleReviewFormChange}
                            >
                              <option value="approved">Approve</option>
                              <option value="rejected">Reject</option>
                            </select>
                          </div>
                          {reviewFormData.status === 'approved' && (
                            <div className="mb-3">
                              <label className="form-label">Set Password:</label>
                              <input 
                                type="password" 
                                name="password" 
                                className="form-control" 
                                placeholder="Enter password for the new account"
                                value={reviewFormData.password}
                                onChange={handleReviewFormChange}
                                required
                              />
                            </div>
                          )}
                          <div className="mb-3">
                            <label className="form-label">Review Notes (Optional):</label>
                            <textarea 
                              name="review_notes" 
                              className="form-control" 
                              rows="3"
                              placeholder="Add any notes about this decision"
                              value={reviewFormData.review_notes}
                              onChange={handleReviewFormChange}
                            ></textarea>
                          </div>
                        </form>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setSelectedRequest(null)}>
                          Cancel
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-primary" 
                          onClick={() => handleReviewRequest(selectedRequest.request_id)}
                          disabled={reviewLoading || (reviewFormData.status === 'approved' && !reviewFormData.password)}
                        >
                          {reviewLoading ? 'Processing...' : `Submit ${reviewFormData.status}`}
        </button>
      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Associates Tab */}
          {activeTab === 'associates' && (
            <div className="associates-tab">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent border-0">
                  <h5 className="card-title mb-0">Associates</h5>
                </div>
                <div className="card-body">
                  {associatesLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
                  ) : associatesError ? (
                    <div className="alert alert-danger" role="alert">
                      {associatesError}
                    </div>
                  ) : associates.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-building fs-1"></i>
                      <p className="mt-2">No associates found</p>
        </div>
      ) : (
        <div className="table-responsive">
                      <table className="table table-hover">
            <thead>
              <tr>
                <th>Company</th>
                <th>Contact Person</th>
                            <th>Industry</th>
                <th>Status</th>
                            <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
                          {associates.map((associate) => (
                            <tr key={associate.associate_id}>
                              <td>{associate.company_name || 'N/A'}</td>
                              <td>{associate.contact_person}</td>
                              <td>{associate.industry}</td>
                              <td>
                                <span className={`badge bg-${associate.verified ? 'success' : 'warning'}`}>
                                  {associate.verified ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                              <td>{new Date(associate.created_at).toLocaleDateString()}</td>
                              <td>
                                <button className="btn btn-sm btn-outline-primary">
                                  View Details
                      </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Freelancers Tab */}
          {activeTab === 'freelancers' && (
            <div className="freelancers-tab">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent border-0">
                  <h5 className="card-title mb-0">Freelancers</h5>
                </div>
                <div className="card-body">
                  {freelancersLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : freelancersError ? (
                    <div className="alert alert-danger" role="alert">
                      {freelancersError}
                    </div>
                  ) : freelancers.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-people fs-1"></i>
                      <p className="mt-2">No freelancers found</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Availability</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {freelancers.map((freelancer) => (
                            <tr key={freelancer.freelancer_id}>
                              <td>{`${freelancer.first_name} ${freelancer.last_name}`}</td>
                              <td>{freelancer.email}</td>
                              <td>
                                <span className={`badge bg-${freelancer.is_approved ? 'success' : 'warning'}`}>
                                  {freelancer.is_approved ? 'Approved' : 'Pending'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge bg-${freelancer.is_available ? 'success' : 'secondary'}`}>
                                  {freelancer.is_available ? 'Available' : 'Unavailable'}
                                </span>
                              </td>
                              <td>
                                <button className="btn btn-sm btn-outline-primary">
                                  View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
              </div>
            </div>
          )}

                     {/* Freelancer Requests Tab */}
           {activeTab === 'freelancer-requests' && (
             <div className="bg-white rounded-4 shadow-sm p-4" style={{ boxShadow: '0 2px 6px rgba(253, 104, 14, 0.1)', border: '2px solid rgba(253, 104, 14, 0.2)', maxWidth: 1200, margin: '0 auto' }}>
               <h5 style={{ color: accent, fontWeight: 700, marginBottom: 18 }}>ECS Employee Freelancer Request Management</h5>
               <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>Review associate requests for freelancer services and provide curated recommendations</p>
               
               {/* Success Message */}
               {recommendationSuccessMessage && (
                 <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
                   <i className="bi bi-check-circle me-2"></i>
                   {recommendationSuccessMessage}
                   <button type="button" className="btn-close" onClick={() => setRecommendationSuccessMessage('')}></button>
            </div>
               )}
              
              {freelancerRequestsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" style={{ color: accent }} role="status"></div>
                  <p className="mt-2 text-muted">Loading freelancer requests...</p>
                </div>
              ) : freelancerRequestsError ? (
                <div className="alert alert-danger">{freelancerRequestsError}</div>
              ) : freelancerRequests.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bi bi-inbox display-4 text-muted"></i>
                  <p className="mt-3 text-muted">No freelancer requests submitted yet</p>
                </div>
              ) : (
                <div className="row g-3">
                  {freelancerRequests.map((request) => (
                    <div key={request.request_id} className="col-12">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                              <h6 className="card-title mb-1" style={{ color: accent, fontWeight: 600 }}>
                                {request.title}
                              </h6>
                              <p className="card-text text-muted small mb-2">
                                {request.description.length > 150 
                                  ? `${request.description.substring(0, 150)}...` 
                                  : request.description}
                              </p>
                  </div>
                            <div className="text-end">
                              <span className={`badge ${
                                request.status === 'pending' ? 'bg-warning' :
                                request.status === 'reviewed' ? 'bg-info' :
                                request.status === 'provided' ? 'bg-success' :
                                request.status === 'completed' ? 'bg-primary' :
                                'bg-secondary'
                              }`}>
                                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              </span>
                </div>
                          </div>
                          
                          <div className="row g-2 mb-3">
                            <div className="col-md-4">
                              <small className="text-muted">
                                <i className="bi bi-building me-1"></i>
                                <strong>Company:</strong> {request.company_name || 'N/A'}
                              </small>
                  </div>
                            <div className="col-md-4">
                              <small className="text-muted">
                                <i className="bi bi-gear me-1"></i>
                                <strong>Skills:</strong> {request.required_skills.join(', ')}
                              </small>
                </div>
                            <div className="col-md-4">
                              <small className="text-muted">
                                <i className="bi bi-calendar me-1"></i>
                                <strong>Submitted:</strong> {new Date(request.created_at).toLocaleDateString()}
                              </small>
                            </div>
                          </div>

                          <div className="row g-2 mb-3">
                            <div className="col-md-3">
                              <small className="text-muted">
                                <i className="bi bi-clock me-1"></i>
                                <strong>Urgency:</strong> {request.urgency_level}
                              </small>
                </div>
                            <div className="col-md-3">
                              <small className="text-muted">
                                <strong>Offered Salary:</strong> {request.budget_range || 'Not specified'}
                              </small>
                            </div>
                            <div className="col-md-3">
                              <small className="text-muted">
                                <i className="bi bi-geo-alt me-1"></i>
                                <strong>Location:</strong> {request.preferred_location || 'Any'}
                              </small>
                            </div>
                            <div className="col-md-3">
                              <small className="text-muted">
                                <i className="bi bi-star me-1"></i>
                                <strong>Experience:</strong> {request.min_experience}+ years
                              </small>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div></div>
                            <button
                              className="btn btn-sm"
                              style={{ 
                                background: (request.recommendation_count && request.recommendation_count > 0) ? '#28a745' : accent, 
                                color: '#fff', 
                                borderRadius: 20,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                transform: 'translateY(0)',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                border: 'none',
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                e.currentTarget.style.background = (request.recommendation_count && request.recommendation_count > 0) ? '#218838' : '#e55a00';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                e.currentTarget.style.background = (request.recommendation_count && request.recommendation_count > 0) ? '#28a745' : accent;
                              }}
                              onMouseDown={(e) => {
                                e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
                              }}
                              onMouseUp={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
                              }}
                              onClick={(e) => {
                                // Add ripple effect
                                const ripple = document.createElement('span');
                                const rect = e.currentTarget.getBoundingClientRect();
                                const size = Math.max(rect.width, rect.height);
                                const x = e.clientX - rect.left - size / 2;
                                const y = e.clientY - rect.top - size / 2;
                                
                                ripple.style.cssText = `
                                  position: absolute;
                                  width: ${size}px;
                                  height: ${size}px;
                                  left: ${x}px;
                                  top: ${y}px;
                                  background: rgba(255, 255, 255, 0.3);
                                  border-radius: 50%;
                                  transform: scale(0);
                                  animation: ripple 0.6s linear;
                                  pointer-events: none;
                                `;
                                
                                e.currentTarget.appendChild(ripple);
                                
                                setTimeout(() => {
                                  ripple.remove();
                                }, 600);
                                
                                // Call the original function
                                openRecommendationsModal(request);
                              }}
                            >
                              <i className="bi bi-star me-1" style={{ 
                                transition: 'transform 0.3s ease',
                                display: 'inline-block'
                              }}></i>
                              {(request.recommendation_count && request.recommendation_count > 0) ? 'Update Recommendations' : 'Provide Recommendations'}
                            </button>
                  </div>
                  </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
                </div>
              )}
              


           {/* Freelancer Request Details Modal */}
           {showFreelancerRequestDetailsModal && selectedFreelancerRequest && (
             <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050 }}>
               <div className="modal-dialog modal-lg">
                 <div className="modal-content">
                   <div className="modal-header">
                     <h5 className="modal-title">Freelancer Request Details</h5>
                     <button type="button" className="btn-close" onClick={() => setShowFreelancerRequestDetailsModal(false)}></button>
                  </div>
                   <div className="modal-body">
                     <div className="row mb-3">
                       <div className="col-md-6">
                         <p><strong>Title:</strong> {selectedFreelancerRequest.title}</p>
                         <p><strong>Description:</strong> {selectedFreelancerRequest.description}</p>
                         <p><strong>Required Skills:</strong> {selectedFreelancerRequest.required_skills.join(', ')}</p>
                         <p><strong>Minimum Experience:</strong> {selectedFreelancerRequest.min_experience} years</p>
                  </div>
                       <div className="col-md-6">
                         <p><strong>Offered Salary:</strong> {selectedFreelancerRequest.budget_range || 'Not specified'}</p>
                         <p><strong>Urgency Level:</strong> {selectedFreelancerRequest.urgency_level}</p>
                         <p><strong>Preferred Location:</strong> {selectedFreelancerRequest.preferred_location || 'Any'}</p>
                </div>
                     </div>
                     
                     <div className="row mb-3">
                       <div className="col-md-6">
                         <p><strong>Status:</strong> 
                           <span className={`badge ms-2 ${
                             selectedFreelancerRequest.status === 'pending' ? 'bg-warning' :
                             selectedFreelancerRequest.status === 'reviewed' ? 'bg-info' :
                             selectedFreelancerRequest.status === 'provided' ? 'bg-success' :
                             selectedFreelancerRequest.status === 'completed' ? 'bg-primary' :
                             'bg-secondary'
                           }`}>
                             {selectedFreelancerRequest.status.charAt(0).toUpperCase() + selectedFreelancerRequest.status.slice(1)}
                           </span>
                         </p>
                         <p><strong>Email:</strong> {selectedFreelancerRequest.associate_email}</p>
                         <p><strong>Company:</strong> {selectedFreelancerRequest.company_name || 'N/A'}</p>
                         <p><strong>Contact Person:</strong> {selectedFreelancerRequest.contact_person}</p>
                         <p><strong>Industry:</strong> {selectedFreelancerRequest.industry}</p>
                         <p><strong>Submitted:</strong> {new Date(selectedFreelancerRequest.created_at).toLocaleDateString()}</p>
                       </div>
                       <div className="col-md-6">
                         <p><strong>Recommendations:</strong> {selectedFreelancerRequest.recommendation_count || 0}</p>
                         <p><strong>Responses:</strong> {selectedFreelancerRequest.response_count || 0}</p>
                         {selectedFreelancerRequest.reviewed_at && (
                           <p><strong>Reviewed:</strong> {new Date(selectedFreelancerRequest.reviewed_at).toLocaleDateString()}</p>
                         )}
                       </div>
                     </div>
            </div>
            <div className="modal-footer">
                     <button type="button" className="btn btn-secondary" onClick={() => setShowFreelancerRequestDetailsModal(false)}>
                       Close
                </button>
                     {selectedFreelancerRequest.status === 'pending' && (
                <button 
                  type="button" 
                         className="btn btn-primary"
                         style={{
                           background: accent,
                           border: 'none',
                           borderRadius: '8px',
                           transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                           transform: 'translateY(0)',
                           boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                           position: 'relative',
                           overflow: 'hidden'
                         }}
                         onMouseEnter={(e) => {
                           e.currentTarget.style.transform = 'translateY(-2px)';
                           e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                           e.currentTarget.style.background = '#e55a00';
                         }}
                         onMouseLeave={(e) => {
                           e.currentTarget.style.transform = 'translateY(0)';
                           e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                           e.currentTarget.style.background = accent;
                         }}
                         onMouseDown={(e) => {
                           e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
                         }}
                         onMouseUp={(e) => {
                           e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
                         }}
                         onClick={(e) => {
                           // Add ripple effect
                           const ripple = document.createElement('span');
                           const rect = e.currentTarget.getBoundingClientRect();
                           const size = Math.max(rect.width, rect.height);
                           const x = e.clientX - rect.left - size / 2;
                           const y = e.clientY - rect.top - size / 2;
                           
                           ripple.style.cssText = `
                             position: absolute;
                             width: ${size}px;
                             height: ${size}px;
                             left: ${x}px;
                             top: ${y}px;
                             background: rgba(255, 255, 255, 0.3);
                             border-radius: 50%;
                             transform: scale(0);
                             animation: ripple 0.6s linear;
                             pointer-events: none;
                           `;
                           
                           e.currentTarget.appendChild(ripple);
                           
                           setTimeout(() => {
                             ripple.remove();
                           }, 600);
                           
                           // Call the original function
                           setShowFreelancerRequestDetailsModal(false);
                           openRecommendationsModal(selectedFreelancerRequest);
                         }}
                       >
                         <i className="bi bi-star me-1" style={{ 
                           transition: 'transform 0.3s ease',
                           display: 'inline-block'
                         }}></i>Provide Recommendations
                </button>
                     )}
                   </div>
                 </div>
               </div>
             </div>
           )}

           {/* Recommendations Modal */}
           {showRecommendationsModal && selectedFreelancerRequest && (
             <div className="modal fade show" data-aos="none" style={{ 
               display: 'block', 
               backgroundColor: 'rgba(0,0,0,0.5)', 
               position: 'fixed', 
               top: 0, 
               left: 0, 
               width: '100%', 
               height: '100%', 
               zIndex: 1050,
               animation: 'none',
               transform: 'none',
               opacity: 1
             }}>
               <div className="modal-dialog modal-xl" data-aos="none" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                 <div className="modal-content" data-aos="none" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                   <div className="modal-header" data-aos="none" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                     <h5 className="modal-title" style={{ 
                       color: accent, 
                       fontWeight: 700,
                       animation: 'none',
                       transform: 'none',
                       opacity: 1
                     }}>All Registered Freelancers</h5>
                     <button type="button" className="btn-close" onClick={() => setShowRecommendationsModal(false)}></button>
                   </div>
                   <div className="modal-body">
                     <div className="row mb-4">
                       <div className="col-md-8">
                         <h6 style={{ color: accent, fontWeight: 600 }}>Request: {selectedFreelancerRequest.title}</h6>
                         <p className="text-muted mb-2">{selectedFreelancerRequest.description}</p>
                         <div className="row g-2">
                           <div className="col-md-4">
                             <small><strong>Skills:</strong> {selectedFreelancerRequest.required_skills.join(', ')}</small>
                           </div>
                           <div className="col-md-4">
                             <small><strong>Experience:</strong> {selectedFreelancerRequest.min_experience}+ years</small>
                           </div>
                           <div className="col-md-4">
                             <small><strong>Location:</strong> {selectedFreelancerRequest.preferred_location || 'Any'}</small>
                           </div>
                         </div>
                       </div>
                       <div className="col-md-4">
                         <div className="mb-3">
                           <label className="form-label">ECS Notes:</label>
                           <textarea
                             className="form-control"
                             rows="3"
                             placeholder="Add any notes about these recommendations..."
                             value={adminNotes}
                             onChange={(e) => setAdminNotes(e.target.value)}
                           ></textarea>
                         </div>
                       </div>
                     </div>

                                           <div className="row mb-3">
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Search by Skills:</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g., React, Node.js"
                            value={searchSkills}
                            onChange={(e) => {
                              setSearchSkills(e.target.value);
                            }}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-bold">Minimum Experience (years):</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="e.g., 3"
                            value={searchExperience}
                            onChange={(e) => {
                              setSearchExperience(e.target.value);
                            }}
                            min="0"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-bold">Status:</label>
                          <select
                            className="form-select"
                            value={searchStatus}
                            onChange={(e) => {
                              setSearchStatus(e.target.value);
                            }}
                          >
                            <option value="all">All Statuses</option>
                            <option value="available">Available</option>
                            <option value="unavailable">Unavailable</option>
                          </select>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                          <div className="d-flex gap-2 w-100">
                <button 
                              className="btn btn-sm w-100" 
                              style={{ 
                                background: accent, 
                                color: '#fff', 
                                height: '38px',
                                transition: 'all 0.2s ease-in-out',
                                transform: 'scale(1)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.05)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.boxShadow = 'none';
                              }}
                              onClick={(e) => {
                                e.target.style.transform = 'scale(0.95)';
                                setTimeout(() => {
                                  e.target.style.transform = 'scale(1)';
                                }, 100);
                                handleSearch();
                              }}
                            >
                              <i className="bi bi-search"></i>
                </button>
                <button 
                              className="btn btn-outline-secondary btn-sm w-100" 
                              style={{ 
                                height: '38px',
                                transition: 'all 0.2s ease-in-out',
                                transform: 'scale(1)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.05)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.boxShadow = 'none';
                              }}
                              onClick={(e) => {
                                e.target.style.transform = 'scale(0.95)';
                                setTimeout(() => {
                                  e.target.style.transform = 'scale(1)';
                                }, 100);
                                setSearchSkills('');
                                setSearchExperience('');
                                setSearchStatus('all');
                                setAvailableFreelancers(allFreelancers);
                              }}
                            >
                              <i className="bi bi-arrow-clockwise"></i>
                </button>
                          </div>
                        </div>
                      </div>

                                           <div className="mb-3">
                        <p className="text-muted mb-0">
                          Showing {availableFreelancers.length} of {allFreelancers.length} freelancers. Use the search above to find specific freelancers.
                        </p>
            </div>

                      <div className="row g-3">
                        {availableFreelancers.map((freelancer) => (
                          <div key={freelancer.freelancer_id} className="col-md-6 col-lg-4">
                            <div className="card shadow-sm h-100 d-flex flex-column" style={{ 
                              border: '2px solid #ffd7c2',
                              animation: 'none',
                              transform: 'none',
                              opacity: 1,
                              visibility: 'visible'
                            }}>
                              <div className="card-body p-3 d-flex flex-column" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                                {/* Header Section */}
                                <div className="d-flex align-items-start mb-3" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                                  <div className="form-check me-2" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={selectedFreelancers.includes(freelancer.freelancer_id)}
                                      onChange={(e) => handleFreelancerSelection(freelancer.freelancer_id, e.target.checked)}
                                      style={{ animation: 'none', transform: 'none', opacity: 1 }}
                                    />
                                  </div>
                                  <div className="flex-grow-1" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                                    <h6 className="card-title mb-1" style={{ 
                                      color: accent, 
                                      fontWeight: 600, 
                                      fontSize: '14px',
                                      animation: 'none',
                                      transform: 'none',
                                      opacity: 1
                                    }}>
                                      {`${freelancer.first_name} ${freelancer.last_name}`}
                                    </h6>
                                  </div>
                                </div>

                                {/* Content Section - Fixed Height */}
                                <div className="flex-grow-1 d-flex flex-column" style={{ 
                                  minHeight: '200px',
                                  animation: 'none',
                                  transform: 'none',
                                  opacity: 1
                                }}>
                                  {/* Role */}
                                  <div className="mb-2">
                                    <strong>Role:</strong> 
                                    <div className="text-muted small">{freelancer.headline || 'Not specified'}</div>
                                  </div>

                                  {/* Skills */}
                                  <div className="mb-2">
                                    <strong>Skills:</strong> 
                                    {freelancer.skills && freelancer.skills.length > 0 && (
                                      <small className="text-muted ms-2">({freelancer.skills.length})</small>
                                    )}
                                    <div className="mt-1" style={{ maxHeight: '60px', overflowY: 'auto' }}>
                                      {freelancer.skills && freelancer.skills.length > 0 ? (
                                        freelancer.skills.map((skill, index) => {
                                          // Handle both new skills structure and legacy structure
                                          const skillName = skill.skill_name || skill;
                                          const proficiencyLevel = skill.proficiency_level;
                                          
                                          return (
                                            <span key={skill.skill_id || index} className="badge bg-light text-dark me-1 mb-1" style={{ fontSize: '10px' }}>
                                              {skillName}
                                              {proficiencyLevel && (
                                                <span className="ms-1" style={{ color: '#666' }}>
                                                  ({proficiencyLevel})
                                                </span>
                                              )}
                                            </span>
                                          );
                                        })
                                      ) : (
                                        <span className="text-muted small">No skills listed</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Experience */}
                                  <div className="mb-2">
                                    <strong>Experience:</strong> 
                                    <span className="text-muted small">{freelancer.experience_years || 0} years</span>
                                  </div>


                                  {/* Contact Info */}
                                  <div className="mb-2">
                                    <strong>Email:</strong> 
                                    <div className="text-muted small text-truncate" title={freelancer.email || 'Not specified'}>
                                      {freelancer.email || 'Not specified'}
                                    </div>
                                  </div>

                                  <div className="mb-2">
                                    <strong>Phone:</strong> 
                                    <div className="text-muted small">{freelancer.phone || 'Not specified'}</div>
                                  </div>

                                  {/* Availability */}
                                  <div className="mb-2" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                                    <strong>Availability:</strong>
                                    <div className="mt-1" style={{ animation: 'none', transform: 'none', opacity: 1 }}>
                                      <span style={{ 
                                        background: freelancer.availability_status === 'available' ? '#d4edda' : '#f8d7da',
                                        color: freelancer.availability_status === 'available' ? '#155724' : '#721c24',
                                        padding: '4px 8px', 
                                        borderRadius: 15,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        animation: 'none',
                                        transform: 'none',
                                        opacity: 1,
                                        visibility: 'visible'
                                      }}>
                                        <i className={`bi ${
                                          freelancer.availability_status === 'available' ? 'bi-check-circle' : 'bi-x-circle'
                                        } me-1`} style={{ animation: 'none', transform: 'none', opacity: 1 }}></i>
                                        {freelancer.availability_status === 'available' ? 'Available for Work' : 'Not Available'}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Hourly Rate */}
                                  {freelancer.hourly_rate && (
                                    <div className="mb-3">
                                      <strong>Hourly Rate:</strong>
                                      <div className="mt-1">
                                        <span style={{ 
                                          background: '#fff3cd', 
                                          color: '#856404', 
                                          padding: '4px 8px', 
                                          borderRadius: 15,
                                          fontSize: 11,
                                          fontWeight: 600
                                        }}>
                                          <i className="bi bi-currency-exchange me-1"></i>
                                          R{freelancer.hourly_rate}/hour
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Footer Section - Always at bottom */}
                                <div className="mt-auto">
                                  <div className="d-flex gap-2 align-items-center mb-2">
                                    <button
                                      type="button"
                                      className="btn btn-sm flex-grow-1"
                                      onClick={() => openFreelancerProfile(freelancer)}
                                      style={{ 
                                        fontSize: '12px',
                                        backgroundColor: '#ffd7c2',
                                        borderColor: '#ffd7c2',
                                        color: '#8b4513',
                                        transition: 'all 0.3s ease-in-out',
                                        transform: 'scale(1)'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.transform = 'scale(1.05)';
                                        e.target.style.backgroundColor = '#ffc299';
                                        e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 194, 0.4)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.transform = 'scale(1)';
                                        e.target.style.backgroundColor = '#ffd7c2';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                      onMouseDown={(e) => {
                                        e.target.style.transform = 'scale(0.95)';
                                      }}
                                      onMouseUp={(e) => {
                                        e.target.style.transform = 'scale(1.05)';
                                      }}
                                    >
                                      <i className="bi bi-person-lines-fill me-1"></i>
                                      View Profile
                                    </button>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={highlightedFreelancers.includes(freelancer.freelancer_id)}
                                      onChange={(e) => handleHighlightFreelancer(freelancer.freelancer_id, e.target.checked)}
                                    />
                                    <label className="form-check-label small">
                                      Highlight as top recommendation
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                   </div>
                   <div className="modal-footer">
                     <button type="button" className="btn btn-secondary" onClick={() => setShowRecommendationsModal(false)}>
                       Cancel
              </button>
              <button 
                         type="button"
                         className="btn"
                         style={{ background: accent, color: '#fff' }}
                         onClick={submitRecommendations}
                         disabled={recommendationsLoading || selectedFreelancers.length === 0}
                       >
                         {recommendationsLoading ? 'Submitting...' : `Submit ${selectedFreelancers.length} Recommendation${selectedFreelancers.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
          </div>
        </div>
           )}

          {/* Freelancer Profile Modal */}
          {showFreelancerProfileModal && selectedFreelancerProfile && (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-xl">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" style={{ color: accent, fontWeight: 600 }}>
                      <i className="bi bi-person-lines-fill me-2"></i>
                      Freelancer Profile - {selectedFreelancerProfile.first_name} {selectedFreelancerProfile.last_name}
                    </h5>
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setShowFreelancerProfileModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    {freelancerProfileLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border" style={{ color: accent }} role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading detailed profile...</p>
                      </div>
                    ) : (
                      <div className="container-fluid">
                        {/* Profile Header */}
                        <div style={{
                          background: '#fff',
                          borderRadius: 20,
                          padding: '32px',
                          marginBottom: 24,
                          boxShadow: '0 4px 32px rgba(0,0,0,0.07)',
                          border: '1px solid #f0f0f0'
                        }}>
                          <div className="row align-items-center">
                            <div className="col-md-3 text-center">
                              {(() => {
                                const BACKEND_URL = API_BASE_URL;
                                let imgUrl = "";
                                const hasCustomImage = !!selectedFreelancerProfile?.profile_picture_url;
                                if (hasCustomImage) {
                                  imgUrl = selectedFreelancerProfile.profile_picture_url.startsWith("http")
                                    ? selectedFreelancerProfile.profile_picture_url
                                    : `${BACKEND_URL}${selectedFreelancerProfile.profile_picture_url}?t=${Date.now()}`;
                                } else {
                                  const name = `${selectedFreelancerProfile?.first_name || ""} ${selectedFreelancerProfile?.last_name || ""}`.trim() || "User";
                                  imgUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${accent.replace('#', '')}&color=fff&size=150&bold=true`;
                                }
                                return (
                                  <img
                                    src={imgUrl}
                                    alt="Profile"
                                    style={{
                                      width: 120,
                                      height: 120,
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: `4px solid ${accent}`,
                                      marginBottom: 16
                                    }}
                                  />
                                );
                              })()}
                              <div className="mt-2 d-flex gap-2 flex-wrap justify-content-between align-items-center">
                                <div className="d-flex gap-2 flex-wrap">
                                <span className={`badge ${
                                  selectedFreelancerProfile.availability_status === 'available' ? 'bg-success' : 'bg-secondary'
                                }`} style={{ fontSize: '12px' }}>
                                  <i className={`bi ${
                                    selectedFreelancerProfile.availability_status === 'available' ? 'bi-check-circle' : 'bi-x-circle'
                                  } me-1`}></i>
                                  {selectedFreelancerProfile.availability_status === 'available' ? 'Available for Work' : 'Not Available'}
                                </span>
                                {selectedFreelancerProfile.hourly_rate && (
                                  <span className="badge" style={{ 
                                    backgroundColor: '#fff3cd', 
                                    color: '#856404', 
                                    fontSize: '12px' 
                                  }}>
                                    <i className="bi bi-currency-exchange me-1"></i>
                                    R{selectedFreelancerProfile.hourly_rate}/hour
                                    </span>
                                  )}
                                </div>
                                {/* Jobs Completed Badge - Top Right */}
                                {selectedFreelancerProfile.completed_jobs && selectedFreelancerProfile.completed_jobs.length > 0 && (
                                  <span style={{ 
                                    background: '#d4edda', 
                                    color: '#155724', 
                                    padding: '8px 16px', 
                                    borderRadius: 20,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    display: 'inline-block',
                                    boxShadow: '0 2px 8px rgba(21, 87, 36, 0.15)'
                                  }}>
                                    <i className="bi bi-briefcase me-2"></i>
                                    {selectedFreelancerProfile.completed_jobs.length} Job{selectedFreelancerProfile.completed_jobs.length !== 1 ? 's' : ''} Completed
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="col-md-9">
                              <h2 style={{ 
                                fontWeight: 700, 
                                color: '#333', 
                                marginBottom: 8 
                              }}>
                                {selectedFreelancerProfile.first_name} {selectedFreelancerProfile.last_name}
                              </h2>
                              {selectedFreelancerProfile.headline && (
                                <h5 style={{ 
                                  color: accent, 
                                  fontWeight: 600, 
                                  marginBottom: 16 
                                }}>
                                  {selectedFreelancerProfile.headline}
                                </h5>
                              )}
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center mb-3">
                                    <i className="bi bi-envelope me-3" style={{ color: accent, fontSize: 18 }}></i>
                                    <span style={{ color: '#666', fontSize: '15px' }}>{selectedFreelancerProfile.email}</span>
                                  </div>
                                  {selectedFreelancerProfile.phone && (
                                    <div className="d-flex align-items-center mb-3">
                                      <i className="bi bi-telephone me-3" style={{ color: accent, fontSize: 18 }}></i>
                                      <span style={{ color: '#666', fontSize: '15px' }}>{selectedFreelancerProfile.phone}</span>
                                    </div>
                                  )}
                                  {selectedFreelancerProfile.address && (
                                    <div className="d-flex align-items-center mb-3">
                                      <i className="bi bi-geo-alt me-3" style={{ color: accent, fontSize: 18 }}></i>
                                      <span style={{ color: '#666', fontSize: '15px' }}>{selectedFreelancerProfile.address}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center mb-3">
                                    <i className="bi bi-briefcase me-3" style={{ color: accent, fontSize: 18 }}></i>
                                    <span style={{ color: '#666', fontSize: '15px' }}>
                                      {selectedFreelancerProfile.experience_years || selectedFreelancerProfile.years_experience || 0} years experience
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center mb-3">
                                    <i className="bi bi-calendar me-3" style={{ color: accent, fontSize: 18 }}></i>
                                    <span style={{ color: '#666', fontSize: '15px' }}>
                                      Member since {new Date(selectedFreelancerProfile.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {selectedFreelancerProfile.current_status && (
                                    <div className="d-flex align-items-center mb-3">
                                      <i className="bi bi-person-check me-3" style={{ color: accent, fontSize: 18 }}></i>
                                      <span className={`badge ${selectedFreelancerProfile.current_status === 'Available' ? 'bg-success' : 'bg-warning'}`}>
                                        {selectedFreelancerProfile.current_status}
                                      </span>
                                    </div>
                                  )}
                                  {selectedFreelancerProfile.website && (
                                    <div className="d-flex align-items-center mb-3">
                                      <i className="bi bi-globe me-3" style={{ color: accent, fontSize: 18 }}></i>
                                      <a href={selectedFreelancerProfile.website} target="_blank" rel="noopener noreferrer" style={{ color: accent, textDecoration: 'none', fontSize: '15px' }}>
                                        Website
                                      </a>
                                    </div>
                                  )}
                                  {selectedFreelancerProfile.linkedin_url && (
                                    <div className="d-flex align-items-center mb-3">
                                      <i className="bi bi-linkedin me-3" style={{ color: accent, fontSize: 18 }}></i>
                                      <a href={selectedFreelancerProfile.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: accent, textDecoration: 'none', fontSize: '15px' }}>
                                        LinkedIn Profile
                                      </a>
                                    </div>
                                  )}
                                  {selectedFreelancerProfile.github_url && (
                                    <div className="d-flex align-items-center mb-3">
                                      <i className="bi bi-github me-3" style={{ color: accent, fontSize: 18 }}></i>
                                      <a href={selectedFreelancerProfile.github_url} target="_blank" rel="noopener noreferrer" style={{ color: accent, textDecoration: 'none', fontSize: '15px' }}>
                                        GitHub Profile
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Professional Summary */}
                        {selectedFreelancerProfile.summary && (
                          <div style={{
                            background: '#fff',
                            borderRadius: 20,
                            padding: '32px',
                            marginBottom: 24,
                            boxShadow: '0 4px 32px rgba(0,0,0,0.07)',
                            border: '1px solid #f0f0f0'
                          }}>
                            <h4 style={{ 
                              fontWeight: 700, 
                              color: '#333', 
                              marginBottom: 16,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10
                            }}>
                              <i className="bi bi-person-lines-fill" style={{ color: accent }}></i>
                              Professional Summary
                            </h4>
                            <p style={{ 
                              color: '#666', 
                              fontSize: 16, 
                              lineHeight: 1.6,
                              margin: 0
                            }}>
                              {selectedFreelancerProfile.summary}
                            </p>
                          </div>
                        )}

                        {/* Education Summary */}
                        {selectedFreelancerProfile.education_summary && (
                          <div style={{
                            background: '#fff',
                            borderRadius: 20,
                            padding: '32px',
                            marginBottom: 24,
                            boxShadow: '0 4px 32px rgba(0,0,0,0.07)',
                            border: '1px solid #f0f0f0'
                          }}>
                            <h4 style={{ 
                              fontWeight: 700, 
                              color: '#333', 
                              marginBottom: 16,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10
                            }}>
                              <i className="bi bi-mortarboard" style={{ color: accent }}></i>
                              Education Summary
                            </h4>
                            <div>
                              {(() => {
                                try {
                                  const education = typeof selectedFreelancerProfile.education_summary === 'string' 
                                    ? JSON.parse(selectedFreelancerProfile.education_summary) 
                                    : selectedFreelancerProfile.education_summary;
                                  
                                  if (Array.isArray(education)) {
                                    return education.map((edu, idx) => (
                                      <div key={idx} style={{ 
                                        marginBottom: idx < education.length - 1 ? 16 : 0,
                                        paddingBottom: idx < education.length - 1 ? 16 : 0,
                                        borderBottom: idx < education.length - 1 ? '1px solid #f0f0f0' : 'none'
                                      }}>
                            <p style={{ 
                                          color: '#333', 
                              fontSize: 16, 
                                          fontWeight: 600,
                                          marginBottom: 4
                                        }}>
                                          {edu.degree} in {edu.field}
                                        </p>
                                        <p style={{ 
                                          color: '#666', 
                                          fontSize: 14,
                                          marginBottom: 2
                                        }}>
                                          {edu.institution}
                                        </p>
                                        <p style={{ 
                                          color: '#999', 
                                          fontSize: 14,
                              margin: 0
                            }}>
                                          {edu.year}
                                        </p>
                                      </div>
                                    ));
                                  }
                                  return <p style={{ color: '#666', fontSize: 16, margin: 0 }}>No education information available</p>;
                                } catch {
                                  return <p style={{ color: '#666', fontSize: 16, margin: 0 }}>{String(selectedFreelancerProfile.education_summary)}</p>;
                                }
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Work History */}
                        {selectedFreelancerProfile.work_history && (
                          <div style={{
                            background: '#fff',
                            borderRadius: 20,
                            padding: '32px',
                            marginBottom: 24,
                            boxShadow: '0 4px 32px rgba(0,0,0,0.07)',
                            border: '1px solid #f0f0f0'
                          }}>
                            <h4 style={{ 
                              fontWeight: 700, 
                              color: '#333', 
                              marginBottom: 16,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10
                            }}>
                              <i className="bi bi-briefcase" style={{ color: accent }}></i>
                              Work History
                            </h4>
                            <div>
                              {(() => {
                                try {
                                  const workHistory = typeof selectedFreelancerProfile.work_history === 'string' 
                                    ? JSON.parse(selectedFreelancerProfile.work_history) 
                                    : selectedFreelancerProfile.work_history;
                                  
                                  if (Array.isArray(workHistory)) {
                                    return workHistory.map((work, idx) => (
                                      <div key={idx} style={{ 
                                        marginBottom: idx < workHistory.length - 1 ? 20 : 0,
                                        paddingBottom: idx < workHistory.length - 1 ? 20 : 0,
                                        borderBottom: idx < workHistory.length - 1 ? '1px solid #f0f0f0' : 'none'
                                      }}>
                            <p style={{ 
                                          color: '#333', 
                              fontSize: 16, 
                                          fontWeight: 600,
                                          marginBottom: 4
                                        }}>
                                          {work.position}
                                        </p>
                                        <p style={{ 
                                          color: '#666', 
                                          fontSize: 14,
                                          marginBottom: 6
                                        }}>
                                          {work.company} • {work.duration}
                                        </p>
                                        {work.description && (
                                          <p style={{ 
                                            color: '#666', 
                                            fontSize: 14,
                              lineHeight: 1.6,
                              margin: 0
                            }}>
                                            {work.description}
                                          </p>
                                        )}
                                      </div>
                                    ));
                                  }
                                  return <p style={{ color: '#666', fontSize: 16, margin: 0 }}>No work history available</p>;
                                } catch {
                                  return <p style={{ color: '#666', fontSize: 16, margin: 0 }}>{String(selectedFreelancerProfile.work_history)}</p>;
                                }
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Skills Section */}
                        {selectedFreelancerProfile.skills && selectedFreelancerProfile.skills.length > 0 && (
                          <div style={{
                            background: '#fff',
                            borderRadius: 20,
                            padding: '32px',
                            marginBottom: 24,
                            boxShadow: '0 4px 32px rgba(0,0,0,0.07)',
                            border: '1px solid #f0f0f0'
                          }}>
                            <h4 style={{ 
                              fontWeight: 700, 
                              color: '#333', 
                              marginBottom: 20,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10
                            }}>
                              <i className="bi bi-star" style={{ color: accent }}></i>
                              Skills & Expertise
                            </h4>
                            <div className="row g-3">
                              {selectedFreelancerProfile.skills.map((skill, index) => (
                                <div key={skill.skill_id || index} className="col-md-6 col-lg-4">
                                  <div style={{
                                    background: '#f8f9fa',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    border: '1px solid #e9ecef',
                                    height: '100%'
                                  }}>
                                    <h6 style={{ 
                                      fontWeight: 600, 
                                      color: '#333',
                                      marginBottom: 8
                                    }}>
                                      {skill.skill_name}
                                    </h6>
                                    {skill.proficiency_level && (
                                      <div className="mb-2">
                                        <span className={`badge ${
                                          skill.proficiency_level === 'Expert' ? 'bg-success' :
                                          skill.proficiency_level === 'Advanced' ? 'bg-primary' :
                                          skill.proficiency_level === 'Intermediate' ? 'bg-warning' : 'bg-secondary'
                                        }`} style={{ fontSize: '10px' }}>
                                          {skill.proficiency_level}
                                        </span>
                                      </div>
                                    )}
                                    {skill.years_experience && (
                                      <small className="text-muted">
                                        {skill.years_experience} years experience
                                      </small>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* CV Information */}
                        {selectedFreelancerProfile.cv && selectedFreelancerProfile.cv.parsed_data && (
                          <div style={{
                            background: '#fff',
                            borderRadius: 20,
                            padding: '32px',
                            marginBottom: 24,
                            boxShadow: '0 4px 32px rgba(0,0,0,0.07)',
                            border: '1px solid #f0f0f0'
                          }}>

                            
                            {/* Work Experience */}
                            {selectedFreelancerProfile.cv.parsed_data.work_experience && (
                              <div className="mb-4">
                                <h5 style={{ color: '#333', fontWeight: 600, marginBottom: 16 }}>
                                  <i className="bi bi-briefcase me-2" style={{ color: accent }}></i>
                                  Work Experience
                                </h5>
                                {selectedFreelancerProfile.cv.parsed_data.work_experience.map((work, index) => (
                                  <div key={index} className="mb-3 p-3" style={{ 
                                    background: '#f8f9fa', 
                                    borderRadius: '8px',
                                    border: '1px solid #e9ecef'
                                  }}>
                                    <h6 style={{ color: '#333', fontWeight: 600, marginBottom: 4 }}>
                                      {work.position || work.job_title || work.title}
                                    </h6>
                                    <p style={{ color: accent, fontWeight: 500, marginBottom: 4 }}>
                                      {work.company}
                                    </p>
                                    <small className="text-muted">
                                      {work.start_date} - {work.end_date || 'Present'}
                                    </small>
                                    {work.description && (
                                      <p style={{ color: '#666', marginTop: 8, marginBottom: 0 }}>
                                        {work.description}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Education */}
                            {selectedFreelancerProfile.cv.parsed_data.education && (
                              <div className="mb-4">
                                <h5 style={{ color: '#333', fontWeight: 600, marginBottom: 16 }}>
                                  <i className="bi bi-mortarboard me-2" style={{ color: accent }}></i>
                                  Education
                                </h5>
                                {(() => {
                                  const education = selectedFreelancerProfile.cv.parsed_data.education;
                                  
                                  // Check if education is an array (for older CVs)
                                  if (Array.isArray(education)) {
                                    return education.map((edu, index) => (
                                  <div key={index} className="mb-3 p-3" style={{ 
                                    background: '#f8f9fa', 
                                    borderRadius: '8px',
                                    border: '1px solid #e9ecef'
                                  }}>
                                    <h6 style={{ color: '#333', fontWeight: 600, marginBottom: 4 }}>
                                      {edu.degree}
                                    </h6>
                                    <p style={{ color: accent, fontWeight: 500, marginBottom: 4 }}>
                                      {edu.institution}
                                    </p>
                                    <small className="text-muted">
                                      {edu.graduation_year || edu.year}
                                    </small>
                                  </div>
                                    ));
                                  } else {
                                    // Education is a string (for newer CVs)
                                    return (
                                      <div className="mb-3 p-3" style={{ 
                                        background: '#f8f9fa', 
                                        borderRadius: '8px',
                                        border: '1px solid #e9ecef'
                                      }}>
                                        <p style={{ color: '#333', marginBottom: 0 }}>
                                          {education}
                                        </p>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        )}


                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => setShowFreelancerProfileModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
       </div>
    </div>
  );
};

export default ECSEmployeeDashboard;
